'use client';

import { capitalized, useProgramNoun } from '@/lib/program-noun';
import { useRef, useState, type FocusEvent, type FormEvent, type KeyboardEvent } from 'react';
import type { LeadFormVariant } from '@/lib/forms/lead-form-variants';
import { HoneypotField } from '@/components/honeypot-field';
import { FormFallbackFields } from '@/components/form-fallback-fields';
import { HONEYPOT_FIELD } from '@/lib/forms/spam';
import { useFormProgressTracking } from '@/lib/analytics/form-progress';

/** The honeypot is never a sign a person started the form. */
const PROGRESS_IGNORED_FIELDS: ReadonlySet<string> = new Set([HONEYPOT_FIELD]);
import { QUOTE_PROGRAM_GROUPS } from '@/lib/quote-programs';
import { Combobox } from '@/components/ui/combobox';
import { DatePicker } from '@/components/ui/date-picker';
import { FormField as Field, fieldHintId } from '@/components/ui/form-field';
import { EMAIL_LABEL } from '@/lib/forms/email-field';
import { COMPANY_LABEL } from '@/lib/forms/company-field';
import { FIELD_BASE } from '@/lib/form-styles';
import { readTrackingPayload } from '@/lib/forms/tracking-payload';
import { LEAD_FORM_VARIANTS } from '@/lib/forms/lead-form-variants';
import { blurFieldError, EMPTY_RECHECK_MS, emptyHintFor, isEmptySubmission, LOST_INPUT_HINT, shouldRecheckEmpty, validateLeadForm } from '@/lib/forms/lead-form-validation';
import { cleanSubmissionFields, valueShape } from '@/lib/forms/field-clean';
import { advancesOnEnter, nextInOrder, TABBABLE_CONTROLS } from '@/lib/forms/enter-key';
import { FIELD_MAX, MESSAGE_NEAR_LIMIT } from '@/lib/forms/field-limits';
import { describeSubmitFailure, type FormErrorBody, type SubmitFailure } from '@/lib/forms/submit-failure';
import { PROGRAM_FIELD } from '@/lib/forms/program-selection';
import {
  SUBMIT_STATUS,
  submitButtonState,
  type SubmitStatus,
} from '@/lib/forms/submit-state';
import { FORM_EVENT, FORM_FAILURE_REASON, failureReasonForStatus, trackFormEvent } from '@/lib/analytics/posthog';
import { goToSuccessPage } from '@/lib/forms/success-navigation';


export type { LeadFormVariant } from '@/lib/forms/lead-form-variants';



/** A characters-left note once the message nears the server's limit. */
function messageHint(length: number): string | undefined {
  if (length < MESSAGE_NEAR_LIMIT) return undefined;
  const left = Math.max(0, FIELD_MAX.message - length);
  return left === 0
    ? `You have reached the ${FIELD_MAX.message.toLocaleString('en-US')} character limit.`
    : `${left.toLocaleString('en-US')} characters left`;
}

/**
 * Names of the boxes the browser marks as autofilled (the :autofill state,
 * -webkit- prefixed in older engines). The state shows even when the value is
 * only a preview the page cannot read; the value itself is never touched.
 */
function autofilledFields(form: HTMLFormElement): string[] {
  const names: string[] = [];
  for (const el of form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input[name], textarea[name]')) {
    for (const selector of [':autofill', ':-webkit-autofill']) {
      try {
        if (el.matches(selector)) {
          names.push(el.name);
          break;
        }
      } catch {
        // This engine does not know the selector; try the next one.
      }
    }
  }
  return names;
}

export function LeadForm({
  variant,
  defaultProgram,
  stacked = false,
  compact = false,
  onDark = false,
  idPrefix = '',
  successPath,
  hiddenFields,
}: {
  variant: LeadFormVariant;
  /**
   * Pre-selects the first program dropdown on the quote form. On a contact
   * variant (the program page's inline card) there is no dropdown, so the
   * program travels as a hidden field instead and the visitor never sees it.
   */
  defaultProgram?: string | undefined;
  /**
   * One field per row. For narrow columns — the BEST Moves sidebar is 360px,
   * where side-by-side inputs are too cramped to fill comfortably.
   */
  stacked?: boolean;
  /**
   * Three rows instead of five: Full name (split into first/last on submit)
   * beside Work email, then Company beside Phone, then the message. For
   * hero cards, where the form competes with the page for height. Contact
   * variants only — the quote variant's event fields keep the full layout.
   */
  compact?: boolean;
  /** Labels styled for a royal/navy card. Inputs stay white either way. */
  onDark?: boolean;
  /**
   * Namespaces every control's DOM id (`${idPrefix}name`, …) so a second copy
   * of this form can coexist on the page — e.g. the global quote modal opening
   * over a page that already has an inline hero form. Field names are never
   * prefixed; the server reads those. Default '' keeps existing markup.
   */
  idPrefix?: string;
  /** Where to go after a successful submit, overriding the variant's page (the showcase stays in its frame). */
  successPath?: string | undefined;
  /** Extra values posted with the form (the showcase key). Never shown. */
  hiddenFields?: Readonly<Record<string, string>> | undefined;
}) {
  const noun = useProgramNoun();
  /** A control's DOM id, namespaced by idPrefix; the field `name` stays raw. */
  const fid = (name: string) => `${idPrefix}${name}`;
  const config = LEAD_FORM_VARIANTS[variant];
  const useCompact = compact && !config.eventFields;
  // Stacked is for narrow columns, but first/last still belong on one line —
  // and the sidebar is 360px inside a wide viewport, so the `sm:` breakpoint
  // would not fire there. That row gets an unconditional two-column grid.
  const nameRowClass = stacked ? 'grid grid-cols-2 gap-3' : 'grid gap-5 sm:grid-cols-2';
  // Compact pairs stay side by side at every width — the 360px sidebar and the
  // phone-width hero card both fit two inputs, and height is what we are saving.
  const pairClass = useCompact
    ? 'grid grid-cols-2 gap-3'
    : stacked
      ? 'grid gap-4'
      : 'grid gap-5 sm:grid-cols-2';
  const [status, setStatus] = useState<SubmitStatus>(SUBMIT_STATUS.idle);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [emptyHint, setEmptyHint] = useState<'lost' | undefined>(undefined);
  /** True during the short second read of an empty form, so a repeat press is ignored, not doubled. */
  const recheckingRef = useRef(false);
  // Per-field validation shown inline. The form is `noValidate`, so these
  // messages — not the browser's easily-missed native bubbles (the cause of
  // the repeated-submit dead ends in session replays) — are what visitors see.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const progress = useFormProgressTracking(variant, PROGRESS_IGNORED_FIELDS);
  const [messageLength, setMessageLength] = useState(0);
  const clearFieldError = (name: string) => {
    setEmptyHint(undefined);
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const { [name]: _removed, ...rest } = prev;
      return rest;
    });
  };
  const ERROR_RING = ' !border-red-500 ring-2 ring-red-500/25';
  const controlClass = (name: string, extra = '') =>
    `${FIELD_BASE}${fieldErrors[name] ? ERROR_RING : ''}${extra ? ` ${extra}` : ''}`;
  const errorAttrs = (name: string) => ({
    'aria-invalid': fieldErrors[name] ? true : undefined,
    'aria-describedby': fieldErrors[name] ? `${fid(name)}-error` : undefined,
    onInput: () => clearFieldError(name),
  });
  /** Scrolls to and focuses the first control with a problem, so mobile visitors land on it. */
  function focusFirstInvalid(form: HTMLFormElement, errors: Record<string, string>) {
    const firstInvalid = Object.keys(errors)[0];
    if (!firstInvalid) return;
    const el = form.querySelector<HTMLElement>(`#${CSS.escape(fid(firstInvalid))}`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.focus({ preventScroll: true });
  }

  /** Reads, cleans and checks the whole form; shared by submit and per-box checks. */
  function checkForm(form: HTMLFormElement) {
    const data = new FormData(form);
    // A number box holding text the browser cannot read submits as ''; say so
    // instead of calling a filled-in field "required".
    const groupSizeInput = form.elements.namedItem('groupSize');
    const unreadableNumbers = new Set<string>(
      groupSizeInput instanceof HTMLInputElement && groupSizeInput.validity.badInput ? ['groupSize'] : [],
    );
    // Cleaned before the check and the send: invisible pasted characters made
    // correct emails fail (field-clean.ts).
    const values = cleanSubmissionFields(
      Object.fromEntries([...data.entries()].map(([key, value]) => [key, typeof value === 'string' ? value : ''])),
    );
    const errors = validateLeadForm(values, {
      // Every layout now has separate first/last name boxes.
      compact: false,
      eventFields: config.eventFields,
      quickEventFields: config.quickEventFields,
      messageRequired: config.messageRequired,
      messageLabel: config.messageLabel,
    }, { unreadableNumbers });
    return { values, errors };
  }

  /**
   * Checks a box as the visitor leaves it (Baymard: flag on completion, not
   * while typing, and not all at once on submit). A filled-in box that is wrong
   * shows its message now; a box that is now right drops its old message. An
   * empty box says nothing until submit, so tabbing past is never scolded.
   */
  function handleBlur(event: FocusEvent<HTMLFormElement>) {
    const field = event.target instanceof HTMLElement ? event.target.getAttribute('name') : null;
    if (!field) return;
    const { values, errors } = checkForm(event.currentTarget);
    const next = blurFieldError(field, values[field], errors);
    setFieldErrors((prev) => {
      if (next === prev[field]) return prev;
      const { [field]: _old, ...rest } = prev;
      return next ? { ...rest, [field]: next } : rest;
    });
  }

  /**
   * Enter in a typing box moves to the next box instead of sending a
   * half-filled form (a tablet's Return/Go key; see enter-key.ts). The last box
   * moves to Send, so a second Enter still sends.
   */
  function handleEnterKey(event: KeyboardEvent<HTMLFormElement>) {
    const form = event.currentTarget;
    const target = event.target;
    if (event.defaultPrevented || !(target instanceof HTMLInputElement) || !form.contains(target)) return;
    if (!advancesOnEnter(event.nativeEvent, target)) return;
    event.preventDefault();
    const controls = [...form.querySelectorAll<HTMLElement>(TABBABLE_CONTROLS)].filter(
      (control) => control.tabIndex >= 0 && !control.matches(':disabled') && control.getClientRects().length > 0,
    );
    nextInOrder<HTMLElement>(controls, target)?.focus();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    trackFormEvent(FORM_EVENT.attempt, { variant });
    progress.markAttempted();

    const form = event.currentTarget;
    const data = new FormData(form);
    let { values, errors } = checkForm(form);
    const activity = { ...progress.activity(), autofilled: autofilledFields(form) };
    let rechecked = false;
    if (Object.keys(errors).length > 0 && isEmptySubmission(values) && shouldRecheckEmpty(activity)) {
      // The boxes read empty although the visitor worked in them or the
      // browser autofilled them: give a just-chosen autofill a moment to land.
      if (recheckingRef.current) return;
      recheckingRef.current = true;
      try {
        await new Promise((resolve) => setTimeout(resolve, EMPTY_RECHECK_MS));
        ({ values, errors } = checkForm(form));
      } finally {
        recheckingRef.current = false;
      }
      rechecked = true;
    }
    if (Object.keys(errors).length > 0) {
      trackFormEvent(FORM_EVENT.failed, {
        variant,
        reason: FORM_FAILURE_REASON.clientValidation,
        invalidFields: Object.keys(errors),
        // The shape of each failing value, never its content (field-clean.ts).
        invalidShapes: Object.fromEntries(Object.keys(errors).map((field) => [field, valueShape(values[field] ?? '')])),
        autofilledFields: activity.autofilled,
        touchedFields: activity.touched,
        typedFields: activity.typed,
        rechecked,
      });
      // Every failed send names the problem under each box, an empty form
      // included (Thomas, Oct 5 2026): a single grey note above the button read
      // as part of the form, and on a phone or iPad it scrolled out of view.
      setFieldErrors(errors);
      // If they typed or the browser autofilled and still nothing came through,
      // also say their details were lost (usually an autofill preview that was
      // never chosen), at the top where the scroll to the first box lands.
      setEmptyHint(isEmptySubmission(values) && emptyHintFor(activity) === 'lost' ? 'lost' : undefined);
      focusFirstInvalid(form, errors);
      return;
    }
    setEmptyHint(undefined);
    setFieldErrors({});
    setStatus(SUBMIT_STATUS.submitting);
    setErrorMessage(undefined);

    const payload: Record<string, unknown> = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: values.phone,
      company: values.company,
      message: values.message,
      tracking: readTrackingPayload(),
    };
    if (config.eventFields) {
      payload.eventCity = values.eventCity;
      payload.eventDate = data.get('eventDate');
      payload.groupSize = values.groupSize;
      payload.programSelection1 = values.programSelection1;
      payload.programSelection2 = data.get('programSelection2') || undefined;
    } else if (config.quickEventFields) {
      payload.groupSize = values.groupSize;
      payload.eventDate = data.get('eventDate') || undefined;
      payload.programSelection1 = values.programSelection1;
    } else if (data.get(PROGRAM_FIELD)) {
      payload[PROGRAM_FIELD] = data.get(PROGRAM_FIELD);
    }
    payload[HONEYPOT_FIELD] = data.get(HONEYPOT_FIELD) || undefined;
    for (const [key, value] of Object.entries(hiddenFields ?? {})) payload[key] = value;

    let failure: SubmitFailure;
    try {
      const response = await fetch(config.endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        trackFormEvent(FORM_EVENT.succeeded, { variant });
        progress.markSucceeded();
        goToSuccessPage(successPath ?? config.successPath);
        return;
      }
      const body = (await response.json().catch(() => null)) as FormErrorBody | null;
      failure = { kind: 'response', status: response.status, body };
    } catch {
      // fetch only throws when the request never completed (offline, blocked).
      failure = { kind: 'network' };
    }
    trackFormEvent(FORM_EVENT.failed, {
      variant,
      reason: failure.kind === 'network' ? FORM_FAILURE_REASON.network : failureReasonForStatus(failure.status),
    });
    const view = describeSubmitFailure(failure, {
      values: payload,
      compact: false,
      hasControl: (name) => form.querySelector(`#${CSS.escape(fid(name))}`) !== null,
    });
    setFieldErrors(view.fieldErrors);
    setStatus(SUBMIT_STATUS.error);
    setErrorMessage(view.message);
    focusFirstInvalid(form, view.fieldErrors);
  }

  const button = submitButtonState({ status, submitLabel: config.submitLabel });

  return (
    <form action={config.endpoint} method="post" onSubmit={handleSubmit} onKeyDown={handleEnterKey} onFocusCapture={progress.onFocusCapture} onInputCapture={progress.onInputCapture} onBlurCapture={handleBlur} noValidate className={`flex flex-col ${useCompact ? 'gap-3.5 short:gap-2' : 'gap-5'}`}>
      <FormFallbackFields next={successPath ?? config.successPath} />
      {Object.entries(hiddenFields ?? {}).map(([key, value]) => (
        <input key={key} type="hidden" name={key} value={value} />
      ))}
      {emptyHint === 'lost' ? (
        <p role="alert" className={`rounded-lg px-4 py-3 text-sm font-medium ${onDark ? 'bg-red-500/20 text-white' : 'bg-red-50 text-red-700'}`}>
          {LOST_INPUT_HINT}
        </p>
      ) : null}
      {useCompact ? (
        // First name + Last name, then Work email on its own row, then Company +
        // Phone (Thomas, Oct 5 2026). A single "Full name" box that had to hold
        // two words, beside Work email (#222), coincided with paid visitors
        // failing validation over and over; separate boxes need no two-word rule.
        // Company never sits beside a name box: visitors read the pair as
        // first/last and typed their surname into Company (Sep 2026).
        <>
          <div className={pairClass}>
            <Field label="First name" htmlFor={fid('firstName')} required dark={onDark} error={fieldErrors.firstName}>
              <input
                enterKeyHint="next"
                id={fid('firstName')}
                name="firstName"
                required
                maxLength={FIELD_MAX.name}
                autoComplete="given-name"
                className={controlClass('firstName')}
                {...errorAttrs('firstName')}
              />
            </Field>
            <Field label="Last name" htmlFor={fid('lastName')} required dark={onDark} error={fieldErrors.lastName}>
              <input
                enterKeyHint="next"
                id={fid('lastName')}
                name="lastName"
                required
                maxLength={FIELD_MAX.name}
                autoComplete="family-name"
                className={controlClass('lastName')}
                {...errorAttrs('lastName')}
              />
            </Field>
          </div>
          <Field label={EMAIL_LABEL} htmlFor={fid('email')} required dark={onDark} error={fieldErrors.email}>
            <input
              enterKeyHint="next"
              id={fid('email')}
              name="email"
              type="email"
              required
              maxLength={FIELD_MAX.email}
              autoComplete="email"
              className={controlClass('email')}
              {...errorAttrs('email')}
              aria-describedby={[fieldHintId(fid('email')), fieldErrors.email ? `${fid('email')}-error` : undefined].filter(Boolean).join(' ')}
            />
          </Field>
          <div className={pairClass}>
            <Field label={COMPANY_LABEL} htmlFor={fid('company')} required dark={onDark} error={fieldErrors.company}>
              <input
                enterKeyHint="next"
                id={fid('company')}
                name="company"
                required
                maxLength={FIELD_MAX.company}
                autoComplete="organization"
                className={controlClass('company')}
                {...errorAttrs('company')}
              />
            </Field>
            <Field label="Phone" htmlFor={fid('phone')} dark={onDark} error={fieldErrors.phone}>
              <input
                enterKeyHint="next"
                id={fid('phone')}
                name="phone"
                type="tel"
                maxLength={FIELD_MAX.phone}
                autoComplete="tel"
                className={controlClass('phone')}
                {...errorAttrs('phone')}
              />
            </Field>
          </div>
        </>
      ) : (
        <>
          <div className={nameRowClass}>
            <Field label="First name" htmlFor={fid('firstName')} required dark={onDark} error={fieldErrors.firstName}>
              <input
                enterKeyHint="next"
                id={fid('firstName')}
                name="firstName"
                required
                maxLength={FIELD_MAX.name}
                autoComplete="given-name"
                className={controlClass('firstName')}
                {...errorAttrs('firstName')}
              />
            </Field>
            <Field label="Last name" htmlFor={fid('lastName')} required dark={onDark} error={fieldErrors.lastName}>
              <input
                enterKeyHint="next"
                id={fid('lastName')}
                name="lastName"
                required
                maxLength={FIELD_MAX.name}
                autoComplete="family-name"
                className={controlClass('lastName')}
                {...errorAttrs('lastName')}
              />
            </Field>
          </div>

          <div className={pairClass}>
            <Field label={EMAIL_LABEL} htmlFor={fid('email')} required dark={onDark} error={fieldErrors.email}>
              <input
                enterKeyHint="next"
                id={fid('email')}
                name="email"
                type="email"
                required
                maxLength={FIELD_MAX.email}
                autoComplete="email"
                className={controlClass('email')}
                {...errorAttrs('email')}
                aria-describedby={[fieldHintId(fid('email')), fieldErrors.email ? `${fid('email')}-error` : undefined].filter(Boolean).join(' ')}
              />
            </Field>
            <Field label="Phone" htmlFor={fid('phone')} dark={onDark} error={fieldErrors.phone}>
              <input
                enterKeyHint="next"
                id={fid('phone')}
                name="phone"
                type="tel"
                maxLength={FIELD_MAX.phone}
                autoComplete="tel"
                className={controlClass('phone')}
                {...errorAttrs('phone')}
              />
            </Field>
          </div>

          {config.eventFields ? (
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={COMPANY_LABEL} htmlFor={fid('company')} required error={fieldErrors.company}>
                <input
                  enterKeyHint="next"
                  id={fid('company')}
                  name="company"
                  required
                  maxLength={FIELD_MAX.company}
                  autoComplete="organization"
                  className={controlClass('company')}
                  {...errorAttrs('company')}
                />
              </Field>
              <Field label="Event city" htmlFor={fid('eventCity')} required error={fieldErrors.eventCity}>
                <input
                  enterKeyHint="next"
                  id={fid('eventCity')}
                  name="eventCity"
                  required
                  maxLength={FIELD_MAX.eventCity}
                  className={controlClass('eventCity')}
                  {...errorAttrs('eventCity')}
                />
              </Field>
            </div>
          ) : (
            <Field label={COMPANY_LABEL} htmlFor={fid('company')} required dark={onDark} error={fieldErrors.company}>
              <input
                enterKeyHint="next"
                id={fid('company')}
                name="company"
                required
                maxLength={FIELD_MAX.company}
                autoComplete="organization"
                className={controlClass('company')}
                {...errorAttrs('company')}
              />
            </Field>
          )}
        </>
      )}

      {useCompact && config.quickEventFields ? (
        <>
          <div className={pairClass}>
            <Field
              label="Number of participants"
              htmlFor={fid('groupSize')}
              required
              dark={onDark}
              error={fieldErrors.groupSize}
            >
              <input
                enterKeyHint="next"
                id={fid('groupSize')}
                name="groupSize"
                type="number"
                min={1}
                step={1}
                inputMode="numeric"
                required
                placeholder="e.g. 150"
                className={controlClass('groupSize')}
                {...errorAttrs('groupSize')}
              />
            </Field>
            <Field label="Event date" htmlFor={fid('eventDate')} dark={onDark}>
              <DatePicker id={fid('eventDate')} name="eventDate" placeholder="Pick a date" />
            </Field>
          </div>
          <Field
            label={capitalized(noun.one)}
            htmlFor={fid('programSelection1')}
            required
            dark={onDark}
            error={fieldErrors.programSelection1}
          >
            <Combobox
              id={fid('programSelection1')}
              name="programSelection1"
              groups={QUOTE_PROGRAM_GROUPS}
              required
              allowEmpty={false}
              invalid={Boolean(fieldErrors.programSelection1)}
              onValueChange={() => clearFieldError('programSelection1')}
              {...(defaultProgram ? { defaultValue: defaultProgram } : {})}
              placeholder={`Choose ${noun.aOne}…`}
            />
          </Field>
        </>
      ) : null}

      {config.eventFields ? (
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Event date" htmlFor={fid('eventDate')}>
            <DatePicker id={fid('eventDate')} name="eventDate" placeholder="Pick a date" />
          </Field>
          <Field label="Group size" htmlFor={fid('groupSize')} required error={fieldErrors.groupSize}>
            <input
              enterKeyHint="next"
              id={fid('groupSize')}
              name="groupSize"
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              required
              placeholder="e.g. 150"
              className={controlClass('groupSize')}
              {...errorAttrs('groupSize')}
            />
          </Field>
        </div>
      ) : null}

      {config.eventFields ? (
        <div className="grid gap-5 sm:grid-cols-2">
          {(
            [
              ['programSelection1', 'Activity choice 1', true],
              ['programSelection2', 'Activity choice 2', false],
            ] as const
          ).map(([name, label, required]) => (
            <Field key={name} label={label} htmlFor={fid(name)} required={required} error={fieldErrors[name]}>
              <Combobox
                id={fid(name)}
                name={name}
                groups={QUOTE_PROGRAM_GROUPS}
                required={required}
                allowEmpty={!required}
                invalid={Boolean(fieldErrors[name])}
                onValueChange={() => clearFieldError(name)}
                {...(required && defaultProgram ? { defaultValue: defaultProgram } : {})}
                placeholder={required ? `Choose ${noun.aOne}…` : `Optional second ${noun.one}…`}
              />
            </Field>
          ))}
        </div>
      ) : null}

      <Field
        label={config.messageLabel}
        htmlFor={fid('message')}
        required={config.messageRequired}
        dark={onDark}
        error={fieldErrors.message}
        hint={messageHint(messageLength)}
      >
        <textarea
          id={fid('message')}
          name="message"
          required={config.messageRequired}
          maxLength={FIELD_MAX.message}
          onChange={(event) => setMessageLength(event.currentTarget.value.length)}
          rows={useCompact ? 3 : 4}
          placeholder={config.messagePlaceholder}
          className={controlClass('message', useCompact ? 'resize-y short:h-16' : 'resize-y')}
          {...errorAttrs('message')}
          aria-describedby={
            [
              messageLength >= MESSAGE_NEAR_LIMIT ? fieldHintId(fid('message')) : undefined,
              fieldErrors.message ? `${fid('message')}-error` : undefined,
            ]
              .filter(Boolean)
              .join(' ') || undefined
          }
        />
      </Field>

      {!config.eventFields && defaultProgram ? (
        <input type="hidden" name={PROGRAM_FIELD} value={defaultProgram} />
      ) : null}
      <HoneypotField />

      {status === SUBMIT_STATUS.error ? (
        <p role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
          {errorMessage}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={button.disabled}
        aria-busy={status === SUBMIT_STATUS.submitting}
        className="mt-1 rounded-full bg-brand-yellow px-8 py-3.5 text-[15px] font-bold text-brand-navy shadow-sm transition-all hover:-translate-y-px hover:bg-brand-yellow-dark hover:shadow-md disabled:translate-y-0 disabled:opacity-60"
      >
        {button.label}
      </button>
    </form>
  );
}
